import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  DOCUMENT,
} from '@angular/core';
import {
  BehaviorSubject,
  switchMap,
  tap,
  NEVER,
  filter,
  merge,
  fromEvent,
  Subject,
  takeUntil,
  map,
  distinctUntilChanged,
  take,
  asyncScheduler,
  observeOn,
} from 'rxjs';

import { focusElement } from '@plugins/client/utils';

import { FocusOutsideService } from '../private/focus-outside/focus-outside.service';

@Directive({
  selector: '[pluginsFocusOutside]',
  exportAs: 'pluginsFocusOutside',
})
export class FocusOutsideDirective implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private focusOutsideService = inject(FocusOutsideService);
  private elt = inject(ElementRef) as ElementRef<HTMLElement>;

  private _focused$ = new BehaviorSubject(false);
  public focused$ = this._focused$.asObservable();

  private _container$ = new BehaviorSubject<HTMLElement | null>(null);
  private container$ = this._container$.asObservable();

  private _miscellaneousFocus = false;
  public get miscellaneousFocus() {
    return this._miscellaneousFocus;
  }

  private _sourceFocusElement: HTMLElement | null = null;
  public get sourceFocusElement() {
    return this._sourceFocusElement;
  }

  private destroy$ = new Subject<void>();

  private sourceFocusElement$ = this.container$.pipe(
    switchMap((container) => {
      if (!container) {
        return NEVER;
      }
      return fromEvent<FocusEvent>(container, 'focusin').pipe(
        take(1),
        map(
          (evt, i) =>
            (this._sourceFocusElement = evt.relatedTarget as HTMLElement),
        ),
      );
    }),
  );

  // check for events that might cause the container to lose focus when it shouldn't
  // ie: current active focused element is disabled, removed from container,...
  private miscContainerFocusEvents$ = this.container$.pipe(
    switchMap((container) => {
      if (!container) {
        return NEVER;
      }
      return merge(
        fromEvent<MouseEvent>(container, 'mouseup'),
        fromEvent<MouseEvent>(container, 'touchend'),
        fromEvent<KeyboardEvent>(container, 'keydown').pipe(
          filter((evt) => ['Enter', ' '].includes(evt.key)),
        ),
      ).pipe(
        tap(() => {
          this._miscellaneousFocus = true;
        }),
        switchMap(() =>
          fromEvent(container, 'focusout').pipe(
            take(1),
            observeOn(asyncScheduler),
            tap(() => {
              this._miscellaneousFocus = false;
              const { activeElement } = this.document;
              if (!container.contains(activeElement)) {
                let focusableElements = Array.from<HTMLElement>(
                  container.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)',
                  ),
                );
                focusableElements = focusableElements.filter(
                  (e) => !e.classList.contains('cdk-visually-hidden'),
                );
                const element = focusableElements[0] ?? this.sourceFocusElement;
                focusElement(element);
              }
            }),
          ),
        ),
      );
    }),
  );

  public get focused(): boolean {
    return this._focused$.getValue();
  }

  public get container() {
    return this._container$.getValue();
  }

  public get anchor() {
    return this.elt.nativeElement;
  }

  ngOnInit(): void {
    merge(
      this.sourceFocusElement$,
      this.miscContainerFocusEvents$,
      this.focused$.pipe(
        distinctUntilChanged(),
        switchMap((focused, i) => {
          if (focused) {
            return this.focusOutsideService.monitor(this);
          }
          if (i) {
            this.focusOutsideService.unmonitor();
          }
          return NEVER;
        }),
        tap(() => this.blur(true)),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public toggle() {
    this._focused$.next(!this._focused$.value);
  }

  public blur(focusSource = false) {
    if (focusSource && this.sourceFocusElement) {
      setTimeout(() => {
        if (
          !this.document.activeElement ||
          this.document.activeElement === this.document.body
        ) {
          focusElement(this.sourceFocusElement);
        }
      });
    }
    this._focused$.next(false);
  }

  public addContainer(elt: HTMLElement) {
    this._container$.next(elt);
  }

  public removeContainer() {
    this._container$.next(null);
  }

  ngOnDestroy(): void {
    if (this.focused) {
      this.focusOutsideService.unmonitor();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
